import { panaceiaCaseRequest } from '../../services/panaceiaRequests.js'

export const REVIEW_CHECKS = [
  ['confirmOfficialSource', 'Conferi o documento na fonte oficial.'],
  ['confirmDocumentIdentity', 'Conferi a identidade do julgado e do processo.'],
  ['confirmTextCorrespondence', 'O trecho corresponde ao inteiro teor e preserva o contexto.'],
  ['confirmIssueApplicability', 'Conferi a pertinência desta referência à questão do caso.'],
  ['confirmProfileUse', 'Conferi a finalidade de uso e o alcance do precedente.'],
  ['acknowledgeStatusLimitations', 'Conferi os limites da verificação de status apresentada.'],
]

export const RESEARCH_STATES = { Pending: 'Aguardando execução', Running: 'Pesquisando',
  Completed: 'Pesquisa concluída', Partial: 'Pesquisa parcial — confira a cobertura',
  Failed: 'Pesquisa não concluída', Cancelled: 'Pesquisa cancelada' }

export const TRANSPORT_LABELS = { Success: 'Consulta concluída', NotAttempted: 'Consulta não realizada',
  AccessDenied: 'Acesso recusado', RateLimited: 'Limite da fonte atingido', TimedOut: 'Tempo da fonte excedido',
  InvalidResponse: 'Resposta da fonte incompatível', Unavailable: 'Fonte indisponível', NotFound: 'Documento não encontrado',
  NoResults: 'Consulta sem resultados', Blocked: 'Consulta bloqueada' }

export const researchContextKey = (fields) => JSON.stringify(panaceiaCaseRequest(fields))
export const researchPending = (job) => ['Pending', 'Running'].includes(job?.state)
export const researchFinished = (job) => ['Completed', 'Partial', 'Failed', 'Cancelled'].includes(job?.state)

export function acceptResearchJob(previous, incoming) {
  if (!previous) return incoming
  if (incoming?.researchId !== previous.researchId || incoming.revision < previous.revision) return previous
  if (incoming.revision === previous.revision && new Date(incoming.updatedAt).valueOf() < new Date(previous.updatedAt).valueOf()) return previous
  return incoming
}

export const validResearchRetry = (previous, incoming) => Boolean(incoming?.researchId === previous.researchId
  && incoming.state === 'Pending' && incoming.revision > previous.revision)

export async function cancelAndRefreshResearchJob({ job, cancel, get, signal, isCurrent, onConfirmed }) {
  const response = await cancel(job.researchId, signal)
  if (signal.aborted || !isCurrent()) return null
  if (!response.success) return response
  if (response.data?.researchId !== job.researchId || response.data.state !== 'Cancelled')
    throw new Error('Não foi possível confirmar o cancelamento.')
  onConfirmed({ ...job, state: 'Cancelled' })
  const refreshRequired = 'O cancelamento foi confirmado. Atualize a pesquisa para obter sua revisão atual antes de retomar.'
  try {
    const latest = await get(job.researchId, signal)
    if (signal.aborted || !isCurrent()) return null
    if (!latest.success || latest.data?.researchId !== job.researchId || !(latest.data.revision > job.revision))
      throw new Error(refreshRequired)
    return latest
  } catch (error) {
    if (signal.aborted) throw error
    throw new Error(refreshRequired)
  }
}

const WARNING_LABELS = {
  SHADOW_NO_DRAFT_INFLUENCE: 'A pesquisa está em modo de observação e não inclui referências na minuta.',
  DOCUMENT_UPDATED_REVALIDATION_QUEUED: 'O documento foi atualizado. A validação e as autorizações anteriores precisam ser refeitas.',
  STATUS_SNAPSHOT_ONLY_NO_LIVE_RECHECK: 'O status disponível vem de um registro anterior; não houve conferência atual na fonte oficial.',
  SOURCE_VERIFICATION_NOT_IMPLEMENTED: 'Esta integração não oferece conferência automática atual da fonte; é necessária conferência humana.',
  SOURCE_RECHECK_UNAVAILABLE: 'A fonte não pôde ser reconferida. A indisponibilidade não comprova inexistência ou vigência.',
  SOURCE_RESOURCE_NOT_FOUND_NOT_LEGAL_INEXISTENCE: 'O documento não foi localizado na fonte; isso não comprova inexistência do julgado.',
  STATUS_HISTORY_CHANGES_REQUIRE_OFFICIAL_REVIEW: 'O histórico de status contém alterações que exigem revisão na fonte oficial.',
  STATUS_OBSERVATION_AFTER_REFERENCE_DATE: 'Há observações posteriores à data de referência, preservadas apenas no histórico.',
  EXACT_RETRIEVAL_POOL_LIMITED_NOT_COMPLETE_COVERAGE: 'A busca atingiu um limite de recuperação e pode deixar outros documentos fora do resultado.',
  STALE_REFERENCE_REQUIRES_NEW_VALIDATION_AND_REVIEW: 'Uma referência perdeu a atualidade e exige nova validação e revisão.',
  SOURCE_CHANGED_OR_UNAVAILABLE_REVIEW_REQUIRED: 'Uma fonte mudou ou ficou indisponível; a referência exige nova conferência.',
  STALE_SOURCE_REQUIRES_NEW_DOCUMENT_REVALIDATION: 'A verificação de uma fonte expirou. Atualize o documento e sua validação.',
  SOURCE_WITHDRAWN_OR_CHANGED: 'Uma fonte foi retirada ou mudou. A associação anterior não foi utilizada.',
  MEMORY_VERSION_NO_LONGER_CURRENT: 'Uma versão documental anterior foi substituída e não foi utilizada.',
  MEMORY_SOURCE_UNAVAILABLE_REVALIDATION_REQUIRED: 'Uma referência recuperada não pôde ser reconferida na fonte e exige atualização.',
  MEMORY_SOURCE_VERSION_CHANGED_REVALIDATION_REQUIRED: 'A versão da fonte de uma referência recuperada mudou e exige atualização.',
  AMBIGUOUS_CANDIDATE_IDENTITY_EXCLUDED: 'Um candidato tinha identidade ambígua e foi excluído.',
  AMBIGUOUS_CANDIDATE_VERSION_EXCLUDED: 'Um candidato tinha versões divergentes e foi excluído.',
  EXTERNAL_SOURCE_VERSION_CHANGED: 'Uma fonte apresentou nova versão; o documento e a revisão anteriores não foram reutilizados.',
  TECHNICAL_STAGE_REQUIRES_REVIEW: 'Uma etapa da pesquisa não foi concluída normalmente e exige conferência.',
  TJSP_OFFICIAL_DOWNLOAD_NOT_LEGAL_STATUS_VERIFICATION: 'O documento foi obtido do TJSP. O download não confirma vigência ou pertinência jurídica.',
  PDF_TEXT_LAYER_EXTRACTED: 'O texto foi extraído das páginas do PDF. Confira a ordem de leitura e o significado no original.',
  MODE_DOES_NOT_AUTHORIZE_DRAFT_REFERENCES: 'O modo atual da pesquisa não autoriza referências na minuta.',
  FAILED_RULE_CANNOT_BE_OVERRIDDEN_BY_REVIEW: 'Há bloqueio de validação que a revisão humana não pode substituir.',
  HUMAN_REVIEW_STALE_OR_OUTSIDE_SCOPE: 'A revisão expirou ou deixou de corresponder ao contexto e à versão documental.',
}

export function researchWarning(value) {
  const text = String(value || '')
  const [code, ...parts] = text.split(':')
  if (WARNING_LABELS[code]) return WARNING_LABELS[code]
  if (/^[A-Z][A-Z0-9_]+$/.test(code)) {
    const explanation = parts.join(':').trim()
    return explanation.includes(' ') ? explanation : 'Há um limite adicional de cobertura ou conferência. Verifique a fonte oficial e o documento antes de usar a referência.'
  }
  return text
}

export function researchError(response) {
  const code = response?.code || response?.errorCode || ''
  if (code === 'LEGAL_RESEARCH_DISABLED') return 'A pesquisa jurisprudencial está desativada no servidor. A geração sem pesquisa continua disponível.'
  if (code === 'LEGAL_RESEARCH_MISCONFIGURED') return 'A configuração da pesquisa está indisponível. Confira a configuração no servidor.'
  if (code === 'JURISPRUDENCE_PROVIDER_DISABLED') return 'Esta integração está desativada. Confira as integrações de jurisprudência antes de associar o documento.'
  if (code === 'LEGAL_RESEARCH_CONFIGURATION_CHANGED') return 'A configuração da pesquisa mudou. Consulte a pesquisa deste contexto novamente para iniciar uma execução com a configuração atual.'
  if (code === 'LEGAL_RESEARCH_NOT_RESUMABLE') return 'Esta pesquisa mudou de estado e não pode ser retomada. Atualize os resultados.'
  if (code.includes('CONFIGURATION') || code.includes('MEMORY_UNAVAILABLE')) return 'Não foi possível consultar a configuração ou a memória da pesquisa. Tente novamente mais tarde.'
  if (response?.status === 409 || code.includes('STALE') || code.includes('CONTEXT'))
    return 'O contexto, a fonte ou a versão da pesquisa mudou. Atualize os resultados e repita a conferência antes de usar a referência.'
  if (response?.status === 429) return 'A pesquisa está ocupada. Aguarde a conclusão das operações e tente novamente.'
  if (response?.status === 403 || response?.status === 401) return 'Sua sessão não permite acessar esta pesquisa.'
  if (response?.status === 404) return 'Esta pesquisa ou documento não está disponível para sua sessão, ou expirou.'
  if (code === 'LEGAL_RESEARCH_SOURCE_TEXT_UNAVAILABLE') return 'Não foi possível obter o inteiro teor deste endereço. Consulte a fonte oficial e envie o documento para conferência.'
  return response?.error || 'Não foi possível concluir a pesquisa. Os dados do formulário foram mantidos.'
}

export function reviewableValidation(validation, document) {
  return Boolean(document && validation?.documentVersionId && validation.documentVersionId === document.documentVersionId
    && validation.documentTextHash === document.textHash && validation.validationHash
    && !validation.rules?.some((rule) => rule.state === 'Fail') && validation.citations?.length
    && validation.citations.every((citation) => citation.state === 'Pass' && citation.verifiedCitation)
    && ['TEXT_INTEGRITY', 'ORIGINAL_INTEGRITY'].every((code) => validation.rules?.some((rule) => rule.code === code && rule.state === 'Pass')))
}

export function hasUsableResearch(job, currentKey, jobKey, eligibility) {
  if (!job || currentKey !== jobKey || !['Completed', 'Partial'].includes(job.state)) return false
  return (job.result?.reviews || []).some((review) => {
    const validation = job.result?.validations?.find((value) => value.validationId === review.validationId)
    return validation && review.validationHash === validation.validationHash
      && eligibility[validation.validationId] === validation.validationHash
      && review.attestation?.decision === 'ApproveReviewedDraft'
      && REVIEW_CHECKS.every(([key]) => review.attestation[key])
  })
}

export function reviewPayload(validation, checks, notes) {
  if (!validation?.validationHash || notes.trim().length < 10 || notes.length > 2000
    || !REVIEW_CHECKS.every(([key]) => checks[key] === true)) throw new Error('Complete as conferências e registre a fundamentação entre 10 e 2.000 caracteres.')
  return { validationHash: validation.validationHash, decision: 'ApproveReviewedDraft',
    ...Object.fromEntries(REVIEW_CHECKS.map(([key]) => [key, true])), notes: notes.trim() }
}

export function validateResearchFile(file) {
  return !file || !/\.(pdf|docx|txt)$/i.test(file.name) || file.size <= 0 || file.size > 2 * 1024 * 1024
    ? 'Selecione o inteiro teor em PDF, DOCX ou TXT, não vazio, de até 2 MB.' : ''
}

export function researchOriginalFileName(versionId, mime) {
  const extension = ({ 'application/pdf': '.pdf', 'text/plain': '.txt',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx' })[mime] || '.bin'
  return `jurisprudencia-${versionId}${extension}`
}

export async function researchFileDocument(file, signal) {
  const failure = validateResearchFile(file)
  if (failure) throw new Error(failure)
  const bytes = new Uint8Array(await file.arrayBuffer())
  if (signal.aborted) throw new DOMException('Operação cancelada.', 'AbortError')
  if (bytes.length <= 0 || bytes.length > 2 * 1024 * 1024) throw new Error('O original deve ter até 2 MB e não pode estar vazio.')
  let binary = ''
  for (let index = 0; index < bytes.length; index += 32768) binary += String.fromCharCode(...bytes.subarray(index, index + 32768))
  return { fileName: file.name, originalBytes: btoa(binary) }
}

export function abortableDelay(milliseconds, signal) {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(new DOMException('Operação cancelada.', 'AbortError')); return }
    const abort = () => { clearTimeout(timer); reject(new DOMException('Operação cancelada.', 'AbortError')) }
    const timer = setTimeout(() => { signal.removeEventListener('abort', abort); resolve() }, milliseconds)
    signal.addEventListener('abort', abort, { once: true })
  })
}

export async function pollResearchJob({ researchId, get, signal, isCurrent, onJob, delay = abortableDelay }) {
  while (!signal.aborted && isCurrent()) {
    await delay(2000, signal)
    if (signal.aborted || !isCurrent()) return
    const response = await get(researchId, signal)
    if (signal.aborted || !isCurrent()) return
    if (!response.success) throw new Error(researchError(response))
    if (response.data?.researchId !== researchId) throw new Error('A resposta não corresponde à pesquisa solicitada.')
    onJob(response.data)
    if (researchFinished(response.data)) return
  }
}
